/**
 * Created by Ning Jiang on 2022-05-03.
 */

import {SerialSymbol} from "./SerialSymbol";
import {SerialSymbolState} from "./SerialSymbolConfig";
import {Serial} from "../Serial";
import {ReelPositionController} from "../reels/ReelPositionController";
import {gsap} from "gsap";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {MultiSprites} from "@nolimitcity/slot-game/bin/animation/MultiSprites";

export class RevealedSymbolPart extends PIXI.Container {

    public symbolName:string;

    private _partHeight:number;
    public get partHeight():number {
        return this._partHeight;
    }

    public set partHeight(value:number) {
        this._partHeight = value;

        this._partMask.height = value;
        this._symbol.position.y = ReelPositionController.DEFAULT_SYMBOL_HEIGHT * 0.5 -
            Math.min(Math.max(value * 0.5, SerialSymbol.SYMBOL_DISPLAY_CENTER_Y[this.symbolName]), ReelPositionController.DEFAULT_SYMBOL_HEIGHT - value * 0.5);
    }

    private _symbol:MultiSprites;
    private _partMask:PIXI.Graphics;
    // private _frameTop:PIXI.Sprite;
    // private _frameBottom:PIXI.Sprite;

    constructor(symName:string) {
        super();

        this.symbolName = symName;

        this._symbol = new MultiSprites();
        this.getStateAnimation(SerialSymbolState.NORMAL,
            {duration : 0.01, alpha : 1},
            {duration : 0.01, alpha : 0});
        this.addChild(this._symbol);

        this._partMask = new PIXI.Graphics();
        this._partMask.beginFill(0x0000FF, 1);
        this._partMask.drawRect(-67, -ReelPositionController.DEFAULT_SYMBOL_HEIGHT * 0.5, 134, ReelPositionController.DEFAULT_SYMBOL_HEIGHT);
        this._partMask.position.set(0, 0);
        this.addChild(this._partMask);
        this._symbol.mask = this._partMask;
    }

    public getStateAnimation(state:SerialSymbolState, inVars:gsap.TweenVars, outVars:gsap.TweenVars, frames?:number[]):gsap.core.Timeline {
        this.createState(state);
        return this._symbol.playSprite(state, inVars, outVars, frames);
    }

    private createState(state:SerialSymbolState):void {
        if(!this._symbol.sprites[state]) {
            this._symbol.addSprites({
                name : state,
                textures : this.getStateTexture(state),
                anchor : new PIXI.Point(0.5, 0.5)
            });
            if(state === SerialSymbolState.NO_WIN) {
                this._symbol.sprites[state].tint = SerialSymbol.noWinTint;
            }
        }
    }

    private getStateTexture(state?:SerialSymbolState):PIXI.Texture[] {
        let texturePath = `${this.symbolName}/${this.symbolName}`;

        if(state === SerialSymbolState.SPIN) {
            texturePath += `_${state}`;
        }

        return Serial.assetLoader.getTextures(texturePath);
    }
}

export class RevealedSymbol extends SerialSymbol {

    public symbolSize:number;

    private _parts:RevealedSymbolPart[];
    private _revealContainer:PIXI.Container;

    constructor(symbolName:string, reelId:number, symbolHeight:number, symbolSize:number) {
        super(symbolName, reelId, symbolHeight, SerialSymbolState.NORMAL);

        this.symbolSize = symbolSize;
        const partHeight = symbolHeight / symbolSize;
        this._parts = [];
        for(let i = 0; i < symbolSize; i++) {
            const part = new RevealedSymbolPart(symbolName);
            part.partHeight = partHeight;
            part.position.set(0, -symbolHeight * 0.5 + (i + 0.5) * partHeight);
            this.addChild(part);
            this._parts.push(part);
        }
        this._revealContainer = new PIXI.Container();
        this.addChild(this._revealContainer);
        this._frameTop.y = this._centerPosition.y - this.actualHeight * 0.5;
        this._frameBottom.y = this._centerPosition.y + this.actualHeight * 0.5;
        this.addChild(this._frameTop);
        this.addChild(this._frameBottom);
    }

    protected initAnimation():void {
        this._centerPosition = this.getCenterPosition();

        const frameFolderName = SerialSymbol.getSymbolFramePath(this.symbolName);
        this._frameTop = new PIXI.Sprite(Serial.assetLoader.getTextures(`frames/${frameFolderName}/top`)[0]);
        this._frameTop.anchor.set(0.5, 0);
        this._frameTop.y = this._centerPosition.y - this.actualHeight * 0.5;
        this._frameBottom = new PIXI.Sprite(Serial.assetLoader.getTextures(`frames/${frameFolderName}/bottom`)[0]);
        this._frameBottom.anchor.set(0.5, 1);
        this._frameBottom.y = this._centerPosition.y + this.actualHeight * 0.5;
    }

    public getStateAnimation(state:SerialSymbolState, inVars:gsap.TweenVars, outVars:gsap.TweenVars, frames?:number[]):gsap.core.Timeline {
        const tl = gsap.timeline();
        this._currentState = state;
        if(this._frameTop) {
            this._frameTop.visible = state != SerialSymbolState.SPIN;
        }
        if(this._frameBottom) {
            this._frameBottom.visible = state != SerialSymbolState.SPIN;
        }
        if(this._parts) {
            this._parts.forEach(part => {
                tl.add(part.getStateAnimation(state, inVars, outVars, frames), 0);
            });
        }
        tl.add(this.getStateColorChangeAnimation(this.getStateTint(state)), 0);

        if(tl.totalDuration() == 0) {
            tl.add(() => {}, 0.1);
        }
        return tl;
    }

    protected updateSymbolHeight() {
        if(!this.transform || !this._parts) {
            return;
        }
        if(this._frameTop) {
            this._frameTop.y = this._centerPosition.y - this.actualHeight * 0.5;
        }
        if(this._frameBottom) {
            this._frameBottom.y = this._centerPosition.y + this.actualHeight * 0.5;
        }

        const partHeight = this.symbolHeight / this.symbolSize;
        this._parts.forEach((part, i) => {
            part.partHeight = partHeight;
            part.position.set(0, -this.symbolHeight * 0.5 + (i + 0.5) * partHeight);
        });
    }

    public playReveal(ani:TimelineSprite):gsap.core.Timeline {
        const mask = new PIXI.Graphics();
        mask.beginFill(0xFF0000, 1);
        mask.drawRect(-ReelPositionController.DEFAULT_SYMBOL_WIDTH * 0.5, -this._symbolHeight * 0.5, ReelPositionController.DEFAULT_SYMBOL_WIDTH, this._symbolHeight);
        mask.endFill();

        const tl = gsap.timeline();
        tl.add(() => {
            ani.hide();
            this._revealContainer.addChild(ani);
            this._revealContainer.addChild(mask);
            ani.mask = mask;
        });
        tl.add(<gsap.core.Timeline><unknown>ani.getAnimationAutoShowHide());
        tl.add(() => {
            ani.parent.removeChild(ani);
            ani.destroy();
            mask.parent.removeChild(mask);
            mask.destroy();
        });

        return tl;
    }

    public playInfectious():TimelineLite {
        const spine = new PIXI.spine.Spine(Serial.assetLoader.getSpineAsset("infectious"));
        spine.autoUpdate = true;
        spine.position.set(this._centerPosition.x, this._centerPosition.y);
        this.addChild(spine)

        const name = this.getInfectiousName();
        spine.state.setAnimation(0, name, false);
        const tl = gsap.timeline();
        tl.add(() => {
            this.removeChild(spine);
            spine.destroy();
        }, spine.spineData.findAnimation(name).duration);
        return tl;
    }

    private getInfectiousName():string {
        if(this.symbolHeight > 150) {
            return `156x${this.symbolSize.toString()}`;
        }

        return `104x${this.symbolSize.toString()}`;
    }
}